// uiarc: alert (https://uiarc.dev/r/alert.json) — CSS Modules yerine Tailwind. Ton değişince simge yerinde dönüşür,
// yeni gösterilen uyarının simgesi küçük bir oturma yapar; başlık ve açıklama bulanıklıktan yükselir, uyarının
// yüksekliği içeriği izler. `open`/`onDismiss` ile kapanırken yükseklik solmayla birlikte çöker, altındaki içerik
// boşluğu kapatır. `danger` `role="alert"`, diğerleri `role="status"`.
import { AlertCircle, Check, Info, TriangleAlert, X } from 'lucide-react'
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type TargetAndTransition,
  type Transition,
} from 'motion/react'
import { useRef, useState, type HTMLAttributes, type ReactNode } from 'react'

import { exitFast, iconIn, iconSwap, shown, textSwap } from '@/components/ui/morph-copy-motion'
import { HeightFrame, Swap } from '@/components/ui/morph-copy'
import { messages } from '@/lib/messages'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

type AlertTone = 'info' | 'success' | 'warning' | 'danger'

interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: AlertTone
  title: string
  children?: ReactNode
  /** Görünürlüğü denetler. Gizlemek yüksekliği çökertip uyarıyı soldurur. */
  open?: boolean
  /** Metnin yanında duran eylem (ör. "Yeniden yükle" düğmesi). */
  action?: ReactNode
  /** Kapat düğmesi gösterir. Denetimsiz uyarı önce çöker, sonra bunu çağırır. */
  onDismiss?: () => void
}

type AlertBoxProps = Omit<AlertProps, 'open'> & { reduce: boolean | null; animateIcon?: boolean }

const icons = { info: Info, success: Check, warning: TriangleAlert, danger: AlertCircle }
const iconTone: Record<AlertTone, string> = {
  info: 'text-brand',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
}

function AlertBox({
  tone = 'info',
  title,
  children,
  className,
  reduce,
  action,
  onDismiss,
  animateIcon = false,
  ...props
}: AlertBoxProps) {
  const Icon = icons[tone]
  const text =
    typeof children === 'string' || typeof children === 'number' ? String(children) : null
  const hasDetails =
    children !== undefined && children !== null && children !== false && children !== ''
  const swap = textSwap(reduce)
  return (
    <div
      {...props}
      data-slot="alert"
      data-tone={tone}
      className={cn(
        'border-border bg-surface rounded-control shadow-resting leading-body flex items-start gap-3 border p-4',
        className,
      )}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      {/* Yeni ton simgeyi yerinde dönüştürür; yeni gösterilen uyarı simgesine küçük bir oturma verir. */}
      <motion.span
        className={cn('relative mt-px grid size-4.5 flex-none place-items-center', iconTone[tone])}
        initial={animateIcon && !reduce ? (iconIn as TargetAndTransition) : false}
        animate={shown}
        transition={motionTokens.spring.snappy}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          <Swap
            key={tone}
            className="relative grid flex-none place-items-center"
            {...iconSwap(reduce)}
          >
            <Icon className="block size-4.5" aria-hidden="true" />
          </Swap>
        </AnimatePresence>
      </motion.span>
      {/* Metin sütunu içeriğin yüksekliğini izler; uzun ya da kısa mesaj uyarıyı sıçratmaz. */}
      <HeightFrame
        className="min-w-0 flex-1"
        reduce={reduce}
        morphKey={`${title}\n${hasDetails}\n${text ?? ''}`}
      >
        <strong className="relative block text-sm font-medium">
          <AnimatePresence mode="popLayout" initial={false}>
            <Swap key={title} className="block" {...swap}>
              {title}
            </Swap>
          </AnimatePresence>
        </strong>
        <AnimatePresence mode="popLayout" initial={false}>
          {hasDetails && (
            <motion.div
              key="details"
              className="text-text-secondary relative mt-1 text-xs"
              {...swap}
            >
              {text === null ? (
                children
              ) : (
                <AnimatePresence mode="popLayout" initial={false}>
                  <Swap key={text} className="block" {...swap}>
                    {text}
                  </Swap>
                </AnimatePresence>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </HeightFrame>
      {action && <div className="-my-1 flex-none self-center">{action}</div>}
      {onDismiss && (
        <button
          type="button"
          className={cn(
            'text-text-muted rounded-pill -my-1.25 -mr-1.5 grid size-7 flex-none cursor-pointer place-items-center border-0 bg-transparent',
            '[transition:transform_var(--duration-spring)_var(--ease-spring),background-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard)]',
            'hover:bg-surface-muted hover:text-foreground',
            'active:scale-[0.96] active:[transition-duration:var(--duration-instant)] active:[transition-timing-function:var(--ease-standard)]',
            'motion-reduce:transition-none motion-reduce:active:scale-100',
          )}
          aria-label={messages.alert.dismiss(title)}
          onClick={onDismiss}
        >
          <X className="size-4" strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

function Alert({ open, onDismiss, ...props }: AlertProps) {
  const reduce = useReducedMotion()
  const [dismissed, setDismissed] = useState(false)
  const shell = useRef<HTMLDivElement>(null)
  const setClip = (value: string) => {
    if (shell.current) shell.current.style.overflow = value
  }
  if (open === undefined && !onDismiss) return <AlertBox {...props} reduce={reduce} />
  const isOpen = open ?? !dismissed
  const dismiss = onDismiss && (() => (open === undefined ? setDismissed(true) : onDismiss()))
  const presence: Transition = reduce
    ? { duration: 0 }
    : {
        height: motionTokens.spring.smooth,
        opacity: { duration: motionTokens.duration.standard, ease: motionTokens.ease.enter },
      }
  const exit: TargetAndTransition = {
    height: 0,
    opacity: 0,
    transition: reduce
      ? { duration: 0 }
      : { height: motionTokens.spring.smooth, opacity: exitFast },
  }
  // Varlık yüksekliği solmayla birlikte çökertir; aşağıdaki içerik sıçramaz, boşluğu kapatır.
  return (
    <AnimatePresence
      initial={false}
      onExitComplete={() => {
        if (open === undefined) onDismiss?.()
      }}
    >
      {isOpen && (
        <motion.div
          key="alert"
          className="min-w-0"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={exit}
          transition={presence}
          ref={shell}
          onAnimationStart={() => setClip('hidden')}
          onAnimationComplete={() => setClip('')}
        >
          <AlertBox {...props} reduce={reduce} onDismiss={dismiss} animateIcon />
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export { Alert }
export type { AlertProps, AlertTone }
